'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';
import { useCookieConsentStore } from '@/stores/cookie-consent.store';
import { isReactNativeWebView } from '@/lib/webview-utils';
import { Button } from '@/components/ui/button/Button';
import { ButtonSize, ButtonVariant } from '@/components/ui/button/Button.types';
import { CookieCustomizationModal } from './CookieCustomizationModal';
import { cookieBannerStyles } from './CookieBanner.style';
import type { CookieBannerProps } from './CookieBanner.types';

/**
 * Links the container to its heading so the landmark carries a name. Static rather than
 * `useId` because exactly one banner is ever mounted — see CookieConsentProvider's test.
 */
const BANNER_TITLE_ID = 'cookie-banner-title';

/**
 * First-visit cookie consent prompt, fixed to the bottom of the viewport.
 *
 * ## Invariants:
 * - The container is a NAMED `region`, never `banner`. Why: `banner` is the site-header
 *   landmark and the real `<header>` already claims it, so declaring it here puts two
 *   indistinguishable "banner" entries in a screen reader's landmark list. `region` is only
 *   exposed as a landmark when it has an accessible name, so the `aria-labelledby` pairing is
 *   load-bearing — drop the name and the prompt vanishes from landmark navigation (PB-5605).
 * - The container is NOT a live region. Why: it holds static copy, and a live region inserted
 *   already containing its text is not reliably announced anyway — so `aria-live` bought
 *   nothing here and mislabelled fixed content as updating.
 */
export const CookieBanner = ({ className }: CookieBannerProps) => {
  const t = useTranslations('CookieConsent');
  const { hasConsent, acceptAll, rejectAll } = useCookieConsentStore();
  const [showCustomize, setShowCustomize] = useState(false);
  const [mounted, setMounted] = useState(false);

  // Defer rendering to the client to avoid hydration mismatch —
  // hasConsent depends on localStorage which isn't available on the server.
  useEffect(() => {
    setMounted(true);
  }, []);

  // Don't show banner if consent already given, inside the native app WebView, or not yet mounted.
  // Suppress only inside the real React Native WebView (the native shell owns consent there) —
  // detected via the JS bridge, not user-agent sniffing, which misclassifies ordinary mobile
  // Safari as a WebView and wrongly hides the banner on first visit (PB-1300).
  if (!mounted || hasConsent || isReactNativeWebView()) return null;

  return (
    <>
      <div
        className={cookieBannerStyles({ className })}
        role="region"
        aria-labelledby={BANNER_TITLE_ID}
        data-cy="cookie-banner"
      >
        <div className="tw:max-w-4xl tw:mx-auto">
          <h2 id={BANNER_TITLE_ID} className="tw:font-bold tw:text-font tw:text-16 tw:mb-small">
            {t('title')}
          </h2>
          <p className="tw:text-14 tw:text-gray-darker tw:mb-base">{t('description')}</p>
          <div className="tw:flex tw:flex-wrap tw:gap-small">
            <Button
              size={ButtonSize.medium}
              variant={ButtonVariant.primary}
              onClick={acceptAll}
              data-cy="cookie-banner-accept-all"
            >
              {t('acceptAll')}
            </Button>
            <Button
              size={ButtonSize.medium}
              variant={ButtonVariant.secondary}
              onClick={rejectAll}
              data-cy="cookie-banner-reject-all"
            >
              {t('rejectAll')}
            </Button>
            <Button
              size={ButtonSize.medium}
              variant={ButtonVariant.secondary}
              onClick={() => setShowCustomize(true)}
              data-cy="cookie-banner-customize"
            >
              {t('customize')}
            </Button>
          </div>
        </div>
      </div>

      <CookieCustomizationModal open={showCustomize} onClose={() => setShowCustomize(false)} />
    </>
  );
};
